// Libraries
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Subscription
import { ADMINISTRATION_TOOLS_LOTS_SUBSCRIPTION, DELETE_LOT_SUBSCRIPTION } from "../api/AdministrationTools/subscriptions";

// Types
import { IAdministrationTools } from "../types/administrationTools/model";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { updateGpsLotsCache } from "../helpers/AdministrationTools/Provider/globalPositioningService";

type TUseLotsSubscription = {
	locationId: string;
	subscriptionKey: string;
	callback: (response: GraphqlSubscriptionResponse<"onUpdateGpsLotsFromIngestion", IAdministrationTools>) => void;
};

const useLotsSubscription = (params: TUseLotsSubscription) => {
	const { locationId, subscriptionKey, callback } = params;
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const lotsSubscriptions = getSubscription(["lotsSubscriptions", locationId, subscriptionKey]);

	lotsSubscriptions?.subscriptions?.onUpdateGpsLotsFromIngestion?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdateGpsLotsFromIngestion", IAdministrationTools>) => callback(response)
	);

	const deleteLotSubscription = getSubscription(["deleteLotSubscriptions", locationId]);
	deleteLotSubscription?.subscriptions?.onDeleteGpsLot?.setCallback(
		(response: GraphqlSubscriptionResponse<"onDeleteGpsLot", { locationId: string; lotId: string }>) => {
			const deletedLotId = response?.data?.onDeleteGpsLot?.lotId;
			if (!deletedLotId) {
				return;
			}
			updateGpsLotsCache(locationId, queryClient, { location: locationId, lotId: deletedLotId });
		}
	);

	useEffect(() => {
		if (locationId) {
			subscriptionKey &&
				addSubscription(
					["lotsSubscriptions", locationId, subscriptionKey],
					[
						{
							context: "onUpdateGpsLotsFromIngestion",
							query: ADMINISTRATION_TOOLS_LOTS_SUBSCRIPTION,
							variables: {
								locationId,
							},
						},
					]
				);
			addSubscription(
				["deleteLotSubscriptions", locationId],
				[
					{
						context: "onDeleteGpsLot",
						query: DELETE_LOT_SUBSCRIPTION,
						variables: {
							locationId,
						},
					},
				]
			);
		}

		return () => {
			deleteSubscription(["lotsSubscriptions", locationId, subscriptionKey]);
			deleteSubscription(["deleteLotSubscriptions", locationId]);
		};
	}, [locationId, addSubscription]);
};

export default useLotsSubscription;
